// Delivery runs: helpers shared by the Orders page, the Create run and Runs
// pop-ups and the run sheet.

// A run takes up to 80 orders (the route sheet's limit, inside the API's 100
// requests a minute).
export const RUN_MAX_STOPS = 80;

export const OPEN_RUN_STATUSES = ["planned", "in_progress"];
// The fulfilment statuses (order_status_id) of a fulfilled order and of
// one at Attention / Partially fulfilled.
export const FULFILLED_ORDER_STATUS = 4;
export const ATTENTION_ORDER_STATUS = 5;

export const isOpenRun = (run) => OPEN_RUN_STATUSES.includes(run?.status);

// Status label and chip tone.
export const RUN_STATUS = {
  planned: ["Planned", "neutral"],
  in_progress: ["In progress", "info"],
  completed: ["Completed", "success"],
  cancelled: ["Cancelled", "danger"],
};

// A stop's outcome on the run, from what the delivery list saved.
export const OUTCOME_FOR_KIND = {
  delivered: "delivered",
  partly: "partly",
  cant: "not_delivered",
};
// The outcomes recorded on the run (the delivery list): the ones that say
// the run delivered, or tried to.
export const RUN_OUTCOMES = Object.values(OUTCOME_FOR_KIND);
// A stop whose order was fulfilled outside the run (order page, bulk, the
// warehouse): done, but not delivered by the run. Only the API sets it.
export const FULFILLED_OUTSIDE = "fulfilled";
export const FULFILLED_OUTSIDE_LABEL = "Fulfilled outside this run";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// The default run date: the next weekday after `from` (Friday → Monday).
export const nextWorkingDay = (from = new Date()) => {
  const day = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 1);
  while (day.getDay() === 0 || day.getDay() === 6) {
    day.setDate(day.getDate() + 1);
  }
  return ymd(day);
};

// "2026-10-28" → "Tue Oct 28" (read as a local date, so it never shifts a day).
export const runDay = (date) => {
  const [year, month, day] = String(date ?? "")
    .slice(0, 10)
    .split("-")
    .map(Number);
  if (!year || !month || !day) return "";
  return new Date(year, month - 1, day)
    .toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    })
    .replace(",", "");
};

// The run's name, else its route and day ("Montréal North · Tue Oct 28").
export const runTitle = (run) =>
  String(run?.name ?? "").trim() ||
  [run?.route?.name, runDay(run?.run_date)].filter(Boolean).join(" · ") ||
  "Run";

// A run as the orders list's Run filter lists it: its title, its day when
// a name doesn't say it, and how it ended once closed ("Montréal North · Tue
// Oct 28 · Completed").
export const runFilterLabel = (run) => {
  const title = runTitle(run);
  const day = runDay(run?.run_date);
  return [
    title,
    day && !title.includes(day) ? day : "",
    isOpenRun(run) ? "" : (RUN_STATUS[run?.status]?.[0] ?? ""),
  ]
    .filter(Boolean)
    .join(" · ");
};

// The runs the Run filter offers (`runs` as the runs list API gives them):
// the open ones first, soonest first, then the others but cancelled ones,
// latest first; `max` at most.
export const runFilterRuns = (runs, max = 12) => {
  const list = Array.isArray(runs) ? runs.filter((run) => run?.id != null) : [];
  const day = (run) => String(run?.run_date ?? "").slice(0, 10);
  const open = list
    .filter(isOpenRun)
    .sort((a, b) => day(a).localeCompare(day(b)) || a.id - b.id);
  const closed = list
    .filter((run) => !isOpenRun(run) && run.status !== "cancelled")
    .sort((a, b) => day(b).localeCompare(day(a)) || b.id - a.id);
  return [...open, ...closed].slice(0, max);
};

// `run` added to `runs`, or put in place of the copy it replaces.
export const upsertRun = (runs, run) => {
  if (run?.id == null) return runs;
  const list = Array.isArray(runs) ? runs : [];
  return list.some((entry) => entry?.id === run.id)
    ? list.map((entry) => (entry?.id === run.id ? { ...entry, ...run } : entry))
    : [run, ...list];
};

// The chip on an order in a run: "Run Tue Oct 28 · stop 3/12".
export const runChipText = (stop) =>
  [
    `Run ${runDay(stop?.run?.run_date)}`.trim(),
    stop?.run?.stops_count != null
      ? `stop ${stop?.position}/${stop.run.stops_count}`
      : "",
  ]
    .filter(Boolean)
    .join(" · ");

// "3 stops", "1 stop".
export const stopsText = (n) => {
  const count = Number(n) || 0;
  return `${count} stop${count === 1 ? "" : "s"}`;
};

// Name parts accounts fill in when they have none ("N/A", "-"), or an empty
// value saved as text ("null"): not a name. "Na" stays (a surname).
const PLACEHOLDER_NAME = /^(n\/a|none|-+|\.+)$/i;
const namePart = (part) => {
  const text = String(part ?? "").trim();
  return PLACEHOLDER_NAME.test(text) || text === "null" || text === "undefined"
    ? ""
    : text;
};

// "Samir K"; else the driver's own name or the username. Placeholder parts
// are skipped ("Tester25 N/A" reads "Tester25").
export const driverName = (driver) =>
  [driver?.firstname, driver?.lastname]
    .map(namePart)
    .filter(Boolean)
    .join(" ") ||
  namePart(driver?.driver?.name) ||
  namePart(driver?.username);

export const driverPhone = (driver) =>
  String(driver?.phone_number || driver?.driver?.phone || "").trim();

// For each order on a run the API returned: the open-run stop the Orders list
// keeps on that row (null once the run is no longer open).
export const openRunStops = (run) => {
  const stops = Array.isArray(run?.stops) ? run.stops : [];
  const open = isOpenRun(run);
  return Object.fromEntries(
    stops.map((stop) => [
      stop.order_id,
      open
        ? {
            id: stop.id,
            run_id: run.id,
            order_id: stop.order_id,
            position: stop.position,
            outcome: stop.outcome,
            run: {
              id: run.id,
              name: run.name,
              run_date: run.run_date,
              status: run.status,
              stops_count: stops.length,
            },
          }
        : null,
    ]),
  );
};

// A locked run keeps its stops as they are (none added, taken off, moved or
// reordered) and can't be cancelled until it's unlocked; its stops can still
// be marked. Only an open run's lock matters.
export const isLockedRun = (run) => isOpenRun(run) && Boolean(run?.locked_at);

// Why a locked run's stops can't change, as the controls' hints say it.
export const LOCKED_HINT = "Unlock the run to change its stops";

// An API time as a Date, or null. "2026-10-28 13:05:00" (no zone) is the
// API's own clock, UTC; ISO times keep their zone.
const apiTime = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return null;
  const date = new Date(
    /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(text)
      ? `${text.replace(" ", "T")}Z`
      : text,
  );
  return Number.isNaN(date.getTime()) ? null : date;
};

// "Locked by Nora B", else "Locked" when the API doesn't say who.
export const lockedByText = (run) => {
  const name =
    driverName(run?.locked_by_user) ||
    (run?.locked_by != null ? `User #${run.locked_by}` : "");
  return name ? `Locked by ${name}` : "Locked";
};

// "Locked by Nora B · Oct 28, 9:05 AM", the time in this device's zone.
export const lockedText = (run) => {
  const when = apiTime(run?.locked_at);
  return [
    lockedByText(run),
    when?.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }),
  ]
    .filter(Boolean)
    .join(" · ");
};

// What closing an open run from its orders would record for the stops
// still to do (`orders`: the orders as the sheet loaded them), as the API
// does: fulfilled orders as fulfilled outside the run, every other one (at
// Attention too) not delivered. Closing never claims a delivery nobody
// recorded. { stops, fulfilled, notDelivered, allFulfilled }, or null when
// there's nothing to close: no stop left, or nothing done on the run yet
// (Cancel run is for a run that didn't happen).
export const runClosing = (run, orders) => {
  if (!isOpenRun(run)) return null;
  const all = Array.isArray(run?.stops) ? run.stops : [];
  const stops = all.filter((stop) => stop?.outcome === "pending");
  if (stops.length === 0) return null;
  const statusOf = new Map(
    (Array.isArray(orders) ? orders : []).map((order) => [
      Number(order?.id),
      Number(order?.order_status_id),
    ]),
  );
  const fulfilled = stops.filter(
    (stop) => statusOf.get(Number(stop.order_id)) === FULFILLED_ORDER_STATUS,
  ).length;
  const done = all.length - stops.length;
  if (done === 0 && fulfilled === 0) return null;
  return {
    stops,
    fulfilled,
    notDelivered: stops.length - fulfilled,
    allFulfilled: fulfilled === stops.length,
  };
};

// How many of the run's stops have an outcome recorded on the run (not
// counting those fulfilled outside it). A run with any is under way:
// cancelling it needs confirm_under_way.
export const recordedStops = (run) =>
  (Array.isArray(run?.stops) ? run.stops : []).filter((stop) =>
    RUN_OUTCOMES.includes(stop?.outcome),
  ).length;

// What the run's status chip says when tapped: when it started or ended,
// and how far it got.
export const runStatusText = (run) => {
  const stops = Array.isArray(run?.stops) ? run.stops : [];
  const count = (outcome) =>
    stops.filter((stop) => stop?.outcome === outcome).length;
  const at = (value) =>
    apiTime(value)?.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  switch (run?.status) {
    case "planned":
      return `Planned${runDay(run.run_date) ? ` for ${runDay(run.run_date)}` : ""}. It starts with the first stop recorded on the delivery list.`;
    case "in_progress": {
      const outside = count(FULFILLED_OUTSIDE);
      return `Started${at(run.started_at) ? ` ${at(run.started_at)}` : ""}. ${recordedStops(run)} of ${stopsText(stops.length)} recorded${outside ? `, ${outside} fulfilled outside the run` : ""}.`;
    }
    case "completed": {
      const outcomes = [
        [count("delivered"), "delivered"],
        [count("partly"), "partly delivered"],
        [count("not_delivered"), "not delivered"],
        [count(FULFILLED_OUTSIDE), "fulfilled outside the run"],
      ]
        .filter(([n]) => n > 0)
        .map(([n, word]) => `${n} ${word}`)
        .join(", ");
      return `Completed${at(run.completed_at) ? ` ${at(run.completed_at)}` : ""}.${outcomes ? ` ${outcomes}.` : ""}`;
    }
    case "cancelled":
      return "Cancelled: nothing more can be recorded on it.";
    default:
      return "";
  }
};

// The API refused to cancel a run because stops are recorded on it (it says
// `under_way: true`): it wants confirm_under_way.
export const isUnderWayRefusal = (response) => {
  const body = response?.error?.response?.data;
  return Boolean(
    body?.under_way || body?.result?.under_way || body?.errors?.under_way,
  );
};

// The route most of the orders share: the run's main route.
export const mainRouteId = (orders) => {
  const counts = new Map();
  orders.forEach((order) => {
    const id = order?.route_id ?? order?.route?.id;
    if (id != null) counts.set(String(id), (counts.get(String(id)) ?? 0) + 1);
  });
  let best = null;
  counts.forEach((count, id) => {
    if (best === null || count > counts.get(best)) best = id;
  });
  return best === null ? null : Number(best);
};
